import { Link, Navigate, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { NativeSelect, SegmentedControl } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, applySorts, useListParams } from "../lib/useListParams";
import { campaignPath, fileWord, stageTone, stageWord, STAGE_WORDS, useUserAccounts } from "../lib/campaign";
import { downloadNarrowed } from "../lib/csvDownload";
import { useCsvColumns } from "../lib/columnChoice";
import { absent, shortDate } from "../lib/format";
import { ListShell } from "../components/ListShell";
import { SequenceLine } from "../components/CampaignSequence";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { DataTable, visibleWith, type ColumnSpec } from "../ui/DataTable";
import { useConfirm } from "../ui/Dialog";
import { RowMenu } from "../ui/Menu";

/* The campaigns list: what are we working on, what is ready for Orum, what
 * is waiting on whom, and start the next one.
 *
 * Each row says the stage, who owns it, where the campaign stands in the
 * engine's one line, how big it is, and when it was last exported. The
 * Owner filter narrows to Mine, No owner or one person. It opens on everyone,
 * because a person who owns campaigns lands on My campaigns already, and a
 * list that narrowed itself for some people would show a builder half the
 * campaigns (docs/ux-decisions.md, Campaign assignment). Active campaigns by
 * default, and Done, Archived and All as segments. Ordered by stage, the
 * ones being called first, then by newest activity. Every fact is
 * api.campaign's (engine migration 200). A row opens the campaign's page.
 * docs/plans/campaigns-overhaul.md, The campaigns list. */
const FILTERS = ["show", "stage", "owner", "state", "by"] as const;

const COLUMNS = "id,name,label,stage,stage_order,archived_at,where_it_stands,properties,people,people_with_mobile,list_count,last_export_kind,last_export_at,created_at,created_by,last_activity_at,owner_name,owner_email,call_on,calling_warning,sequence_words,sequence_length,follows_name,follows_label,followed_by_name,followed_by_label";

const CSV_DEFAULT = ["label", "stage", "owner_name", "sequence_words", "call_on", "where_it_stands", "properties", "people", "people_with_mobile", "last_export_kind", "last_export_at", "created_at", "created_by"] as const;

const SHOW = [
  { value: "", label: "Active" },
  { value: "done", label: "Done" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

/** A measured figure with its separators, or a dash that says why. */
const num = (n: number | null | undefined) =>
  n === null || n === undefined ? absent("-", "Not measured yet") : n.toLocaleString();

export function Campaigns({ session }: { session: SignedIn }) {
  const params = useListParams("stage_order", FILTERS, true, "campaign");
  const csvColumns = useCsvColumns("campaigns", "campaign", CSV_DEFAULT);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const accounts = useUserAccounts(session.idToken);
  const show = params.filters.show ?? "";
  /* The Owner filter is Mine, No owner, or one person by address, so a link
     to it reads in the address bar. Mine is whoever opens the link. */
  const ownerWords = (v: string) => v === "mine" ? "Mine" : v === "none" ? "No owner"
    : accounts.data?.find((u) => u.email === v)?.label ?? v;

  const narrowed = (opts?: { count: "exact"; head?: boolean }, columns?: string) => {
    let q = apiClient(session.idToken).from("campaign").select(columns ?? COLUMNS, opts);
    if (show === "") q = q.is("archived_at", null).in("stage", ["draft", "enriching", "ready_to_call", "calling"]);
    else if (show === "done") q = q.is("archived_at", null).in("stage", ["completed", "abandoned"]);
    else if (show === "archived") q = q.not("archived_at", "is", null);
    const s = params.search.trim().replace(/[(),*"\\]/g, " ").trim();
    if (s) q = q.or(`label.ilike.*${s}*,name.ilike.*${s}*`);
    if (params.filters.stage) q = q.eq("stage", params.filters.stage);
    const owner = params.filters.owner;
    if (owner === "mine") q = q.eq("owner_email", session.email.toLowerCase());
    else if (owner === "none") q = q.is("owner_id", null);
    else if (owner) q = q.eq("owner_email", owner);
    if (params.filters.state) q = q.filter("filters->state_in", "cs", JSON.stringify([params.filters.state.toUpperCase()]));
    if (params.filters.by) q = q.ilike("created_by", `%${params.filters.by.replace(/[%,()]/g, "")}%`);
    return q;
  };

  const query = useQuery({
    queryKey: ["campaigns", params.page, params.search, params.sorts, params.filters],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error, count } = await applySorts(narrowed({ count: "exact" }), params.sorts)
        .order("last_activity_at", { ascending: false, nullsFirst: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as Campaign[], total: count ?? null };
    },
  });

  if (params.openRecord) return <Navigate to={campaignPath(params.openRecord)} replace />;

  const rows = query.data?.rows ?? [];
  const archive = (c: Campaign) => confirm.ask({
    title: `Archive ${c.label}?`, says: "It leaves the campaigns list and keeps its history.", label: "Archive", danger: true,
    run: () => callFunction(session.idToken, "archive_criteria_set", { p_name: c.name, p_archived: true })
      .then(() => queryClient.invalidateQueries({ queryKey: ["campaigns"] })),
  });

  const columns: ColumnSpec<Campaign>[] = [
    { id: "label", header: "Campaign", role: "leading", fields: ["label"], sort: "label", title: true, narrowLabel: null,
      cell: (c) => <Link to={campaignPath(c.name ?? "")} state={{ from: "campaigns" }} className="link">{c.label}</Link> },
    { id: "stage", header: "Stage", role: "leading", fields: ["stage"], sort: "stage_order", narrowLabel: null,
      cell: (c) => c.archived_at ? <Badge tone="quiet">Archived</Badge> : <Badge tone={stageTone(c.stage)}>{stageWord(c.stage)}</Badge> },
    { id: "owner", header: "Owner", role: "secondary", fields: ["owner_name"], sort: "owner_name",
      // "None" under the Owner heading, which a narrow row reads as "Owner None".
      cell: (c) => c.owner_name ?? <span className="muted">None</span> },
    /* Where it is in its owner's sequence, in the engine's words (migration
       311), with the campaigns it names as links. It sorts by the engine's
       sequence_order, which puts each owner's sequences together, each in
       its order (migration 314), so sorting by it shows every rep's lineup. */
    { id: "sequence", header: "Sequence", role: "secondary", fields: ["sequence_words"], sort: "sequence_order", narrowLabel: null,
      cell: (c) => (c.sequence_length ?? 1) > 1 && c.sequence_words ? <SequenceLine c={c} /> : absent("None", "Not in a sequence") },
    /* The estimated start, the day the team expects to start calling (engine
       migration 256, worded by James on 2026-11-02). Sorting by it shows the
       order campaigns will be called in. Red when the engine warns that it
       will be missed. */
    { id: "calling", header: "Estimated start", role: "secondary", fields: ["call_on"], sort: "call_on", narrowLabel: "estimated start",
      cell: (c) => c.call_on
        ? <span className={c.calling_warning ? "refusal" : undefined}>{shortDate(c.call_on)}</span>
        : absent("Not set", "No estimated starting date") },
    { id: "where", header: "Where it stands", role: "secondary", fields: ["where_it_stands"], narrowLabel: null,
      cell: (c) => c.where_it_stands ?? absent() },
    { id: "properties", header: "Properties", role: "secondary", fields: ["properties"], align: "num", sort: "properties", narrowLabel: "properties",
      cell: (c) => num(c.properties ?? c.list_count) },
    { id: "people", header: "People", role: "detail", fields: ["people"], align: "num", sort: "people",
      cell: (c) => num(c.people) },
    { id: "mobile", header: "With a mobile", role: "secondary", fields: ["people_with_mobile"], align: "num", sort: "people_with_mobile", narrowLabel: "with a mobile",
      cell: (c) => num(c.people_with_mobile) },
    { id: "export", header: "Last export", role: "secondary", fields: ["last_export_at"], sort: "last_export_at", sortDescendingFirst: true, narrowLabel: null,
      cell: (c) => c.last_export_at ? <>{fileWord(c.last_export_kind)}, {shortDate(c.last_export_at)}</> : <span className="muted">Not yet</span> },
    { id: "made", header: "Made", role: "detail", fields: ["created_at"], sort: "created_at", sortDescendingFirst: true,
      cell: (c) => shortDate(c.created_at) },
  ];

  return (
    <>
      <ListShell
        title="Campaigns"
        params={params}
        csvColumns={csvColumns}
        total={query.data?.total ?? null}
        rowCount={rows.length}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        isError={query.isError}
        error={query.error as Error | null}
        dataUpdatedAt={query.dataUpdatedAt}
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ["campaigns"] })}
        onRetry={() => query.refetch()}
        onDownload={() => downloadNarrowed({
          name: "campaigns",
          columns: csvColumns.chosen,
          total: query.data?.total ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await applySorts(narrowed(undefined, csvColumns.chosen.join(",")), params.sorts)
              .order("last_activity_at", { ascending: false, nullsFirst: false }).range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as unknown as Campaign[];
          },
        })}
        searchLabel="Search by name"
        emptyEverything={show === "" ? "No active campaigns. New campaign starts one."
          : show === "done" ? "No completed or abandoned campaigns."
          : show === "archived" ? "No archived campaigns." : "No campaigns yet. New campaign starts one."}
        filterLabels={{ stage: "Stage", owner: "Owner", state: "State", by: "Made by" }}
        filterWords={{ owner: ownerWords }}
        filters={
          <>
            <NativeSelect label="Stage" value={params.filters.stage ?? ""}
                          data={[{ value: "", label: "Any" }, ...Object.entries(STAGE_WORDS).map(([value, label]) => ({ value, label }))]}
                          onChange={(e) => params.set({ filters: { ...params.filters, stage: e.currentTarget.value } })} />
            <NativeSelect label="Owner" value={params.filters.owner ?? ""}
                          error={accounts.isError ? (accounts.error as Error).message : undefined}
                          data={[{ value: "", label: "Anyone" }, { value: "mine", label: "Mine" }, { value: "none", label: "No owner" },
                                 ...(accounts.data ?? []).map((u) => ({ value: u.email ?? "", label: u.label ?? u.email ?? "" }))]}
                          onChange={(e) => params.set({ filters: { ...params.filters, owner: e.currentTarget.value } })} />
            <label className="cl-filter">
              <span className="field-label">State</span>
              <input defaultValue={params.filters.state ?? ""} key={`s-${params.filters.state ?? ""}`} maxLength={2} size={3}
                     onBlur={(e) => params.set({ filters: { ...params.filters, state: e.currentTarget.value.trim().toUpperCase() } })}
                     onKeyDown={(e) => { if (e.key === "Enter") params.set({ filters: { ...params.filters, state: e.currentTarget.value.trim().toUpperCase() } }); }} />
            </label>
            <label className="cl-filter">
              <span className="field-label">Made by</span>
              <input defaultValue={params.filters.by ?? ""} key={`b-${params.filters.by ?? ""}`} size={12}
                     onBlur={(e) => params.set({ filters: { ...params.filters, by: e.currentTarget.value.trim() } })}
                     onKeyDown={(e) => { if (e.key === "Enter") params.set({ filters: { ...params.filters, by: e.currentTarget.value.trim() } }); }} />
            </label>
          </>
        }
        beforeList={
          <div className="cs-top">
            <SegmentedControl value={show} data={SHOW} aria-label="Which campaigns"
                              onChange={(v) => params.set({ filters: { ...params.filters, show: v } })} />
            <Button kind="orange" onClick={() => navigate("/campaigns/new")}>New campaign</Button>
          </div>
        }
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(c) => c.name ?? ""}
          params={params}
          visible={visibleWith(csvColumns.chosen)}
          onOpen={(c) => navigate(campaignPath(c.name ?? ""), { state: { from: "campaigns" } })}
          rowMenu={(c) => (
            <RowMenu items={[
              { label: "Open", onSelect: () => navigate(campaignPath(c.name ?? ""), { state: { from: "campaigns" } }) },
              { label: "Duplicate", onSelect: () => navigate(`/campaigns/new?copy=${encodeURIComponent(c.name ?? "")}`) },
              ...(c.archived_at ? [] : [{ label: "Archive", danger: true, onSelect: () => archive(c) }]),
            ]} />
          )}
        />
      </ListShell>
      {confirm.dialog}
    </>
  );
}
